import React, { useState, useEffect, useRef } from "react";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import styles from "./css/AudioPlayer.module.css";
import axios from "axios";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useSelector } from "react-redux";

interface Chapter {
  number: number;
  filename: string;
  duration?: number; // Total duration of the chapter
  progress?: number; // Current progress in the chapter
}

const AudioPlayerComponent = () => {
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [currentChapter, setCurrentChapter] = useState<number>(1);
  const [availableChapters, setAvailableChapters] = useState<Chapter[]>([]);
  const playerRef = useRef<any>(null);
  const language = useSelector((state: any) => state.language.value);
  const [chapterDurations, setChapterDurations] = useState<{
    [key: number]: number;
  }>({});

  // Helper function to format time in MM:SS
  const formatTime = (timeInSeconds: number): string => {
    if (!timeInSeconds) return "00:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Load saved progress for all chapters
  const loadChapterProgress = () => {
    const progress: { [key: number]: number } = {};
    availableChapters.forEach((chapter) => {
      const savedTime = localStorage.getItem(`chapter-${chapter.number}-time`);
      if (savedTime) {
        progress[chapter.number] = parseFloat(savedTime);
      }
    });
    return progress;
  };

  // Load saved chapter and its time on mount
  useEffect(() => {
    const fetchInitialState = async () => {
      try {
        // First get available chapters
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/audio/chapters`
        );
        setAvailableChapters(response.data);

        // Then load saved chapter if it exists
        const savedChapter = localStorage.getItem("audio-current-chapter");
        if (savedChapter) {
          const chapterNum = parseInt(savedChapter);
          // Verify the chapter exists in available chapters
          if (response.data.some((ch: Chapter) => ch.number === chapterNum)) {
            console.log("Restoring saved chapter:", chapterNum);
            setCurrentChapter(chapterNum); // This will trigger the audio fetch useEffect
          } else {
            console.log(
              "Saved chapter not found in available chapters, defaulting to 1"
            );
            setCurrentChapter(1);
          }
        } else {
          console.log("No saved chapter found, defaulting to 1");
          setCurrentChapter(1);
        }
      } catch (error) {
        console.error("Error loading initial state:", error);
        setCurrentChapter(1); // Default to chapter 1 on error
      }
    };

    fetchInitialState();
  }, []); // Empty dependency array means this runs once on mount

  // Fetch chapter audio
  useEffect(() => {
    const fetchAudioStream = async () => {
      try {
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }

        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/audio/chapter/${currentChapter}`,
          {
            responseType: "blob",
          }
        );
        const audioBlob = response.data;
        const newAudioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(newAudioUrl);
      } catch (error) {
        console.error("Error fetching audio:", error);
      }
    };

    if (currentChapter) {
      fetchAudioStream();
    }

    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [currentChapter]);

  const handleChapterChange = (chapterNumber: number) => {
    // Save current time before changing chapter
    if (playerRef.current?.audio?.current) {
      const currentTime = playerRef.current.audio.current.currentTime;
      if (currentTime > 0) {
        localStorage.setItem(
          `chapter-${currentChapter}-time`,
          currentTime.toString()
        );
      }
    }

    // Save the new chapter number immediately
    localStorage.setItem("audio-current-chapter", chapterNumber.toString());
    console.log("Saving current chapter:", chapterNumber);

    setCurrentChapter(chapterNumber);
  };

  return (
    <div className={styles.productContainer}>
      <span className={styles.productTitle}>
        {language === "RO" ? "Player Audio" : "Audio Player"}
      </span>

      <div className={styles.productInfo}>
        {audioUrl && (
          <AudioPlayer
            ref={playerRef}
            src={audioUrl}
            autoPlayAfterSrcChange={false}
            onLoadedData={(e) => {
              // Get duration when audio is loaded
              if (playerRef.current?.audio?.current) {
                const duration = playerRef.current.audio.current.duration;
                setChapterDurations((prev) => ({
                  ...prev,
                  [currentChapter]: duration,
                }));
              }

              // Existing time restoration logic
              const savedTime = localStorage.getItem(
                `chapter-${currentChapter}-time`
              );
              console.log(
                `Loading chapter ${currentChapter}, saved time:`,
                savedTime
              );

              if (savedTime && playerRef.current?.audio?.current) {
                const timeValue = parseFloat(savedTime);
                if (Number.isFinite(timeValue) && timeValue > 0) {
                  setTimeout(() => {
                    playerRef.current.audio.current.currentTime = timeValue;
                    console.log(
                      `Restored chapter ${currentChapter} to time:`,
                      timeValue
                    );
                  }, 100);
                }
              }
            }}
            onListen={(e) => {
              // Save time periodically for current chapter
              if (e.target.currentTime > 0) {
                localStorage.setItem(
                  `chapter-${currentChapter}-time`,
                  e.target.currentTime.toString()
                );
                // Force re-render of chapter list to update progress
                setChapterDurations((prev) => ({ ...prev }));
              }
            }}
            listenInterval={1000}
            className={styles.audioPlayer}
            customStyles={{
              backgroundColor: "transparent",
              boxShadow: "none",
              width: "100%",
              "& .rhap_main": {
                display: "flex",
                alignItems: "center",
              },
              "& .rhap_controls-section": {
                margin: "0 20px",
              },
              "& .rhap_progress-bar": {
                backgroundColor: "#e5e5e5",
              },
              "& .rhap_progress-filled": {
                backgroundColor: "#517059",
              },
              "& .rhap_progress-indicator": {
                backgroundColor: "#517059",
              },
              "& .rhap_play-pause-button": {
                color: "#517059",
                fontSize: "40px",
              },
              "& .rhap_forward-button, & .rhap_rewind-button": {
                color: "#517059",
              },
              "& .rhap_time": {
                color: "#3a382f",
                fontSize: "14px",
              },
            }}
          />
        )}
      </div>

      <div className={styles.chaptersContainer}>
        {availableChapters.map((chapter) => {
          const duration = chapterDurations[chapter.number] || 0;
          const progress = parseFloat(
            localStorage.getItem(`chapter-${chapter.number}-time`) || "0"
          );

          return (
            <div
              key={chapter.number}
              className={`${styles.chapterItem} ${
                currentChapter === chapter.number ? styles.activeChapter : ""
              }`}
              onClick={() => handleChapterChange(chapter.number)}
            >
              {/* <PlayArrowIcon className={styles.playIcon} /> */}
              <div className={styles.chapterInfo}>
                <div className={styles.chapterHeader}>
                  <span className={styles.chapterTitle}>
                    {language === "RO"
                      ? `Capitolul ${chapter.number}`
                      : `Chapter ${chapter.number}`}
                  </span>
                  <span className={styles.chapterTime}>
                    {formatTime(progress)} / {formatTime(duration)}
                  </span>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{
                      width: duration
                        ? `${(progress / duration) * 100}%`
                        : "0%",
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AudioPlayerComponent;
